import { CurrencyPipe, DatePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DocumentSubFolderName } from '@models/common/document-sub-folder-name.enum';
import { EntityType } from '@models/common/entity-type.enum';
import { Company } from "@models/companies/company";
import { Document } from "@models/documents/document";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { DocumentStepEnum } from "@models/documents/document-step.enum";
import { LodgementDeadline } from "@models/documents/lodgement-deadline";
import { Trust } from "@models/trusts/trust";
import { FilesResponse } from "@models/ui-models/files";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import { TagComponent } from "@shared/components/commons/tag/tag.component";
import {
  RemindersSidebarComponent
} from "@shared/components/documents-related/reminders-sidebar/reminders-sidebar.component";
import {
  AgDocumentSigningStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-signing-status/ag-document-signing-status.component";
import {
  AgDocumentStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-status/ag-document-status.component";
import {
  AgTagEntityTitleComponent
} from "@shared/components/grid/ag-cells/common/ag-tag-entity-title/ag-tag-entity-title.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { DocumentCommonService } from '@shared/services/documents-common.service';
import { DocumentsService } from "@shared/services/documents.service";
import { FilesApiService } from "@shared/services/files-api.service";
import { FilesService } from "@shared/services/files.service";
import { SignalRService } from "@shared/services/signalr.service";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, catchError, combineLatest, filter, of, ReplaySubject, switchMap, tap, } from "rxjs";
import { DocumentsListAccordionComponent } from "../documents-list-accordion/documents-list-accordion.component";

@Component({
  selector: 'app-generate-documents-sidebar',
  standalone: true,
  imports: [
    ButtonComponent,
    NgxSkeletonLoaderModule,
    DatePipe,
    TagComponent,
    CurrencyPipe,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    AgTagEntityTitleComponent,
    RemindersSidebarComponent,
    AgDocumentStatusComponent,
    DocumentsListAccordionComponent,
    NgbDropdownItem,
    AgDocumentSigningStatusComponent
  ],
  templateUrl: 'generate-documents-sidebar.component.html',
  styleUrl: 'generate-documents-sidebar.component.scss'
})
export class GenerateDocumentsSidebarComponent implements OnInit {
  documentsService: DocumentsService = inject(DocumentsService);
  documentCommonService = inject(DocumentCommonService);
  signalRService = inject(SignalRService);
  filesService = inject(FilesService);
  filesApiService = inject(FilesApiService);
  cdr = inject(ChangeDetectorRef);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  @Input() isEntityLoading = false;
  @Input() entityType = EntityType.Company;
  @Input() entity: Company | Trust | null = null;
  @Input() currentStepIndex: number | undefined;
  @Input() lodgement: LodgementDeadline | undefined;

  @Input() set document(value: Document) {
    if (value) {
      this._document = value;
      this.document$.next(this._document);
    }
  }

  readonly DocumentSubFolderName = DocumentSubFolderName;
  readonly document$ = new ReplaySubject<Document>(1);
  readonly refreshList$ = new BehaviorSubject(false);
  readonly DocumentStatusEnum = DocumentStatusEnum;
  readonly DocumentStepEnum = DocumentStepEnum;
  readonly EntityType = EntityType;

  loading = false;
  _document: Document | undefined;
  documentsList: FilesResponse | null = null;
  signatureDate: string | undefined;

  constructor() {
    this.toastr.toastrConfig.positionClass = 'toast-top-right';
  }

  ngOnInit(): void {
    this.listenSignalRMessages();
    this.listenDocumentsListChanges();
  }

  listenDocumentsListChanges(): void {
    combineLatest([
      this.refreshList$,
      this.document$
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.loading = true;
          this.cdr.detectChanges();
        }),
        filter(([_, document]) => !!document),
        switchMap(([_, document]) => this.filesApiService.getFiles(document.documentId)),
        tap(() => this.loading = false),
      )
      .subscribe({
        next: (documentsList) => {
          this.documentsList = documentsList;
        },
        error: (error) => {
          console.warn('[listenDocumentsListChanges] error: ', error);
        }
      });
  }

  public listenSignalRMessages(): void {
    this.signalRService
      .onDocumentEnvelopeCompleted()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (message) => {
          if (message === this.document?.documentId) {
            console.log('Envelope complete processing for message:', message);
            this.refreshList$.next(true);
          }
        },
        error: (err) => {
          console.error('Error handling SignalR message:', err);
        },
      });
  }

  downloadFile(fileName: string, subFolder: string) {
    this.documentCommonService.downloadFile(this._document!.documentId, fileName, subFolder);
  }

  deleteFile(fileName: string): void {
    this.documentCommonService.deleteFile(this._document!.documentId, fileName).pipe(
      tap((result: boolean) => {
        if (result)
          this.refreshList$.next(true);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  downloadCombinedPDF(subFolder: string): void {
    this.filesApiService
      .downloadFolder(this._document!.documentId, true, subFolder)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download 'Generated documents.pdf' error", "Error");
          return of('ERR');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res !== 'ERR') {
          downloadBase64File(res, `Generated documents.pdf`);
        }
      });
  }

  downloadZIP(subFolder: string): void {
    this.filesApiService
      .downloadFolder(this._document!.documentId, false, subFolder)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download 'Generated documents.zip' error", "Error");
          return of('ERR');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res !== 'ERR') {
          downloadBase64File(res, "Generated documents.zip");
        }
      });
  }

  openUploadFileModal(subfolder: DocumentSubFolderName): void {
    const fileUploadConfig = { allowedMaxFileSizeMB: 30, acceptedFormatsList: [] };
    this.filesService.openUploadFilesModal(this._document?.documentId!, subfolder, fileUploadConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshList$.next(true));
  }

  get showRemindersSidebar(): boolean {
    return this.currentStepIndex === DocumentStepEnum.SignDocuments && this._document !== undefined && !Document.isImportedDocument(this._document);
  }
}
